import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, BookOpen, Brain, Trophy } from 'lucide-react';
import { useAuth } from '../lib/auth';

const HomePage = () => {
  const { user } = useAuth();

  return (
    <div className="relative">
      {/* Hero Section with Single Image */}
      <div className="relative bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:w-1/2 lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="sm:text-center lg:text-left">
                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block">Booste tes révisions</span>
                  <span className="block text-secondary-600">du Brevet et du Bac avec l'IA !</span>
                </h1>
                <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                  Transforme tes cours en fiches de révision, et teste tes connaissances avec des quiz interactifs et des flashcards.
                </p>
                <div className="mt-5 sm:mt-8 sm:flex sm:flex-col sm:justify-center lg:justify-start space-y-3">
                  {user && (
                    <div className="rounded-md shadow">
                      <Link
                        to="/grand-quiz"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 md:py-4 md:text-lg md:px-10"
                      >
                        <img 
                          src="/image palmier.png" 
                          alt="Palmier" 
                          className="h-6 w-6 mr-2 filter brightness-0 invert"
                        />
                        Le Grand Quiz Spécial Vacances
                      </Link>
                    </div>
                  )}
                  <div className="rounded-md shadow">
                    <Link
                      to={user ? "/nouvelle-fiche" : "/register"}
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-secondary-600 hover:bg-secondary-700 md:py-4 md:text-lg md:px-10"
                    >
                      {user ? "Créer une fiche de révision" : "Commencer gratuitement"}
                    </Link>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
        
        {/* Single Image Container */}
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 relative">
          <div className="relative h-56 sm:h-72 md:h-96 lg:h-full overflow-hidden">
            <Link to={user ? "/grand-quiz" : "/register"} className="block h-full w-full">
              <img
                className="h-full w-full object-cover"
                src="/visuel vacances.jpg"
                alt="Le Grand Quiz Spécial Vacances"
              />
              
              {/* Overlay avec contenu du Grand Quiz Vacances */}
              <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                <div className="text-center text-white p-6">
                  <span className="inline-block px-3 py-1 rounded-full text-sm font-semibold mb-3 bg-gradient-to-r from-green-500 to-blue-500">
                    Spécial Vacances
                  </span>
                  <h2 className="text-2xl md:text-3xl font-bold mb-2 drop-shadow-lg">
                    Le Grand Quiz Spécial Vacances
                  </h2>
                  <p className="text-lg md:text-xl mb-4 drop-shadow-lg">
                    Révise sans stress pour être au top à la rentrée et grimpe dans le classement !
                  </p>
                  <span className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md transition-colors text-white bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600">
                    <img 
                      src="/image palmier.png" 
                      alt="Palmier" 
                      className="h-6 w-6 mr-2 filter brightness-0 invert"
                    />
                    Commencer le Grand Quiz
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-secondary-600 font-semibold tracking-wide uppercase">Fonctionnalités</h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Une nouvelle façon d'apprendre
            </p>
          </div>

          <div className="mt-10">
            <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-3 md:gap-x-8 md:gap-y-10">
              <div className="relative">
                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-secondary-500 text-white">
                  <Sparkles className="h-6 w-6" />
                </div>
                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Fiches personnalisées</p>
                <p className="mt-2 ml-16 text-base text-gray-500">
                  Notre IA analyse vos cours avec reconnaissance d'écriture et crée des fiches de révision personnalisées.
                </p>
              </div>

              <div className="relative">
                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-secondary-500 text-white">
                  <BookOpen className="h-6 w-6" />
                </div>
                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Quiz Interactifs</p>
                <p className="mt-2 ml-16 text-base text-gray-500">
                  Testez vos connaissances avec des quiz générés automatiquement.
                </p>
              </div>

              <div className="relative">
                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-secondary-500 text-white">
                  <Brain className="h-6 w-6" />
                </div>
                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">Flashcards</p>
                <p className="mt-2 ml-16 text-base text-gray-500">
                  Révisez efficacement avec des flashcards personnalisées.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;